import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Minus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import ofuesiLogo from "@/assets/ofuesi-logo.png";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

interface ChatButton { label: string; href?: string; action?: "contact"; intent?: string; }
interface ChatMsg {
  from: "user" | "bot";
  text: string;
  buttons?: ChatButton[];
}

interface Region {
  id: string;
  name: string;
  coordinator_name: string | null;
  coordinator_email: string | null;
  coordinator_phone: string | null;
  states: string[];
  description: string | null;
}

interface Member {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  role: string;
  region_id: string | null;
}

interface Facilitator {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  region_id: string | null;
}

interface Activity {
  id: string;
  title: string;
  description: string | null;
  schedule: string | null;
  zoom_link: string | null;
  meeting_id: string | null;
  passcode: string | null;
  region_id: string | null;
  activity_type?: string;
}

interface Conference {
  id: string;
  conference_name: string;
  description: string | null;
  location: string | null;
  conference_date: string | null;
  region_id: string | null;
  status: string;
}

// City to State mapping for common cities
const cityToStateMap: Record<string, string> = {
  // Texas
  "austin": "Texas", "dallas": "Texas", "houston": "Texas", "san antonio": "Texas", "fort worth": "Texas",
  "el paso": "Texas", "arlington": "Texas", "corpus christi": "Texas", "plano": "Texas",
  // California
  "los angeles": "California", "san francisco": "California", "san diego": "California", "sacramento": "California",
  "san jose": "California", "fresno": "California", "oakland": "California", "long beach": "California",
  "anaheim": "California", "santa ana": "California", "riverside": "California", "irvine": "California",
  // New York
  "new york": "New York", "buffalo": "New York", "rochester": "New York", "syracuse": "New York",
  "albany": "New York", "yonkers": "New York",
  // Illinois
  "chicago": "Illinois", "naperville": "Illinois",
  // Georgia
  "atlanta": "Georgia", "savannah": "Georgia", "athens": "Georgia", "augusta": "Georgia",
  // Florida
  "miami": "Florida", "orlando": "Florida", "tampa": "Florida", "jacksonville": "Florida",
  "tallahassee": "Florida", "fort lauderdale": "Florida", "st petersburg": "Florida",
  // Washington
  "seattle": "Washington", "spokane": "Washington", "tacoma": "Washington", "vancouver": "Washington",
  "bellevue": "Washington",
  // Oregon
  "portland": "Oregon", "salem": "Oregon", "eugene": "Oregon", "gresham": "Oregon", "hillsboro": "Oregon",
  // Massachusetts
  "boston": "Massachusetts", "cambridge": "Massachusetts", "worcester": "Massachusetts", "lowell": "Massachusetts",
  // Pennsylvania
  "philadelphia": "Pennsylvania", "pittsburgh": "Pennsylvania", "allentown": "Pennsylvania",
  // Arizona
  "phoenix": "Arizona", "tucson": "Arizona", "mesa": "Arizona", "chandler": "Arizona", "scottsdale": "Arizona",
  // Michigan
  "detroit": "Michigan", "grand rapids": "Michigan", "warren": "Michigan", "ann arbor": "Michigan",
  // Colorado
  "denver": "Colorado", "colorado springs": "Colorado", "fort collins": "Colorado",
  // North Carolina
  "charlotte": "North Carolina", "raleigh": "North Carolina", "greensboro": "North Carolina", "durham": "North Carolina",
  // Tennessee
  "nashville": "Tennessee", "memphis": "Tennessee", "knoxville": "Tennessee", "chattanooga": "Tennessee",
  // Ohio
  "cleveland": "Ohio", "cincinnati": "Ohio", "toledo": "Ohio", "akron": "Ohio",
  // Indiana
  "indianapolis": "Indiana", "fort wayne": "Indiana", "evansville": "Indiana",
  // Missouri
  "kansas city": "Missouri", "st louis": "Missouri",
  // Wisconsin
  "milwaukee": "Wisconsin", "madison": "Wisconsin", "green bay": "Wisconsin",
  // Minnesota
  "minneapolis": "Minnesota", "st paul": "Minnesota",
  // Louisiana
  "new orleans": "Louisiana", "baton rouge": "Louisiana", "shreveport": "Louisiana",
  // Oklahoma
  "oklahoma city": "Oklahoma", "tulsa": "Oklahoma", "norman": "Oklahoma",
};

// Shared helper functions available to both intent handlers and text processing
const matchesKeywords = (text: string, keywords: string[]): boolean => {
  return keywords.some(keyword => text.includes(keyword.toLowerCase()));
};

const formatActivityDetails = (activity: Activity): string => {
  let details = `📅 ${activity.title}`;
  if (activity.description) details += `\n${activity.description}`;
  if (activity.schedule) details += `\n⏰ Schedule: ${activity.schedule}`;
  if (activity.zoom_link) details += `\n🔗 Zoom Link: ${activity.zoom_link}`;
  if (activity.meeting_id) details += `\n🆔 Meeting ID: ${activity.meeting_id}`;
  if (activity.passcode) details += `\n🔑 Passcode: ${activity.passcode}`;
  return details;
};

// Role labels for leadership display
const roleLabel: Record<string, string> = {
  president: "President",
  vice_president: "Vice President",
  secretary: "Secretary",
  treasurer: "Treasurer",
  prayer_joint_secretary: "Prayer/Joint Secretary",
  ex_officio: "Ex-Officio",
  national_council: "National Council",
};

// Executive Committee roles
const ecRoles = [
  "president",
  "vice_president",
  "secretary",
  "treasurer",
  "prayer_joint_secretary",
  "ex_officio",
];

export const ChatbotWidget = () => {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [region, setRegion] = useState<string>("");
  const [input, setInput] = useState("");
  const [regions, setRegions] = useState<Region[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [facilitators, setFacilitators] = useState<Facilitator[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [conferences, setConferences] = useState<Conference[]>([]);
  const [loading, setLoading] = useState(true);
  const [awaitingState, setAwaitingState] = useState(false);
  const [awaitingCity, setAwaitingCity] = useState(false);
  const [awaitingFeedbackRating, setAwaitingFeedbackRating] = useState(false);
  const [awaitingFeedbackComment, setAwaitingFeedbackComment] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState<number | null>(null);
  const [showIntents, setShowIntents] = useState(false);
  const [showRegionSelector, setShowRegionSelector] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [awaitingFirstName, setAwaitingFirstName] = useState(false);
  const [awaitingLastName, setAwaitingLastName] = useState(false);
  const [awaitingLocation, setAwaitingLocation] = useState(false);
  const [inputPlaceholder, setInputPlaceholder] = useState("Type your message...");
  const [unknownAttempts, setUnknownAttempts] = useState(0);
  const [pendingIntent, setPendingIntent] = useState<string | null>(null);
  const [onboardingStep, setOnboardingStep] = useState<"country" | "userinfo" | "region" | "chat">("country");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [countryOther, setCountryOther] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState<string>("");
  const [messages, setMessages] = useState<ChatMsg[]>([
    { from: "bot", text: "Welcome to OFUESI! 👋\n\nPlease select your location below to get started." },
  ]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Create session when chatbot opens
  useEffect(() => {
    if (open && !sessionId && onboardingStep === "chat") {
      createChatSession();
    }
  }, [open, onboardingStep, sessionId]);

  // Fetch all data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [regionsRes, membersRes, facilitatorsRes, activitiesRes, conferencesRes] = await Promise.all([
          supabase.from("regions").select("id, name, coordinator_name, coordinator_email, coordinator_phone, states, state_codes, description, bible_studies, prayer_cells").order("name"),
          supabase.from("members").select("id, full_name, email, phone, role, region_id").eq("is_active", true),
          supabase.from("facilitators").select("id, full_name, email, phone, region_id"),
          supabase.from("activities").select("id, title, description, schedule, zoom_link, meeting_id, passcode, region_id, activity_type").eq("is_active", true),
          supabase.from("conferences").select("id, conference_name, description, location, conference_date, region_id, status").eq("is_active", true),
        ]);

        if (regionsRes.error) throw regionsRes.error;
        if (membersRes.error) throw membersRes.error;
        if (facilitatorsRes.error) throw facilitatorsRes.error;
        if (activitiesRes.error) throw activitiesRes.error;
        if (conferencesRes.error) throw conferencesRes.error;

        const parsedRegions = (regionsRes.data || []).map((r: any) => {
          let bibleStudies = r.bible_studies;
          if (Array.isArray(bibleStudies)) {
            bibleStudies = bibleStudies.map((s: any) => {
              if (typeof s === "string") {
                try { return JSON.parse(s); } catch { return null; }
              }
              return s;
            }).filter(Boolean);
          }
          return { ...r, bible_studies: bibleStudies };
        });

        setRegions(parsedRegions);
        setMembers(membersRes.data || []);
        setFacilitators(facilitatorsRes.data || []);
        setActivities(activitiesRes.data || []);
        setConferences(conferencesRes.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const regionColorMap: Record<string, string> = {
    Northwest: "hsl(var(--ministry-purple))",
    Midwest: "hsl(var(--ministry-gold))",
    Eastern: "hsl(var(--ministry-rose))",
    Southeast: "hsl(var(--ministry-emerald))",
    Southern: "hsl(var(--ministry-deep))",
    Southwest: "hsl(var(--ministry-cyan))",
  };

  const headerBorderColor = region
    ? regionColorMap[region] || "hsl(var(--ministry-blue))"
    : "hsl(var(--ministry-blue))";

  useEffect(() => {
    containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  // Create chat session
  const createChatSession = async () => {
    if (sessionId) return;
    
    try {
      const regionData = regions.find(r => r.name === region);
      const { data, error } = await supabase
        .from('chat_sessions')
        .insert({
          user_first_name: userFirstName || 'Anonymous',
          user_last_name: userLastName || '',
          user_location: selectedCountry === 'USA' ? stateProvince : countryOther,
          region_id: regionData?.id || null,
          is_international: selectedCountry !== 'USA'
        })
        .select()
        .single();
      
      if (!error && data) {
        setSessionId(data.id);
        console.log('Chat session created:', data.id);
      }
    } catch (error) {
      console.error("Error creating chat session:", error);
    }
  };

  // Save message to database
  const saveMessage = async (from: "user" | "bot", text: string, intent?: string) => {
    if (!sessionId) return;
    
    try {
      await supabase.from("chat_messages").insert({
        session_id: sessionId,
        message_from: from,
        message_text: text,
        intent: intent || null,
      });
    } catch (error) {
      console.error("Error saving message:", error);
    }
  };

  // Handle quick intents without typing into the input
  const processIntent = async (intent: string) => {
    setShowIntents(false);

    // Ensure we collect user info first
    if (awaitingFirstName) {
      setPendingIntent(intent);
      setInputPlaceholder("Type your first name...");
      const last = messages[messages.length - 1];
      if (!(last?.from === "bot" && /first name/i.test(last.text))) {
        const botMsg = "Before we continue, please type your first name.";
        setMessages((m) => [...m, { from: "bot", text: botMsg }]);
        await saveMessage("bot", botMsg);
      }
      return;
    }
    if (awaitingLastName) {
      setPendingIntent(intent);
      const botMsg = "Please type your last name to continue.";
      setMessages((m) => [...m, { from: "bot", text: botMsg }]);
      setInputPlaceholder("Type your last name...");
      await saveMessage("bot", botMsg);
      return;
    }
    if (awaitingLocation) {
      setPendingIntent(intent);
      const botMsg = "Are you currently in the USA or international?";
      setMessages((m) => [...m, { from: "bot", text: botMsg }]);
      setInputPlaceholder("Type 'USA' or your country...");
      await saveMessage("bot", botMsg);
      return;
    }

    const lower = intent.toLowerCase();

    // Helpdesk / Contact Form intent
    if (intent === "contact_helpdesk" || lower.includes("helpdesk") || lower.includes("contact form")) {
      const botMsg = "I'll connect you with our help desk. Please fill out the contact form and our team will get back to you shortly.";
      setMessages((m) => [...m, { 
        from: "bot", 
        text: botMsg,
        buttons: [
          { label: "Open Contact Form", href: "/contact" },
          { label: "← Back to Main Menu", intent: "main_menu" }
        ]
      } as ChatMsg]);
      await saveMessage("bot", botMsg, "contact_helpdesk");
      
      // Mark session as routed to contact
      if (sessionId) {
        await supabase.from("chat_sessions")
          .update({ routed_to_contact: true })
          .eq("id", sessionId);
      }
      return;
    }

    // Main Menu intent
    if (intent === "main_menu") {
      setShowIntents(true);
      setAwaitingCity(false);
      setAwaitingState(false);
      const menuMsg = `🏠 Main Menu:\n\nHow can I help you today? Select an option below or type your question!`;
      setMessages((m) => [...m, { from: "bot", text: menuMsg }]);
      await saveMessage("bot", menuMsg, "main_menu");
      setInputPlaceholder("Type your question or choose an option...");
      return;
    }

    // New to USA intent
    if (intent === "new_to_usa") {
      setAwaitingCity(true);
      const bibleVerse = "\"For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future.\" - Jeremiah 29:11 🙏";
      const botMsg = `Welcome to the USA! 🎉\n\n${bibleVerse}\n\nWe're excited to support you in your journey! Please tell me which city and state you're in or moving to so I can connect you with your regional fellowship.\n\n(e.g., Austin, Texas or just Portland)`;
      setMessages((m) => [...m, { from: "bot", text: botMsg }]);
      setInputPlaceholder("City, State (e.g., Austin, Texas)");
      await saveMessage("bot", botMsg);
      return;
    }

    // Regional contacts intent - show region buttons
    if (intent === "regional_contacts") {
      setOnboardingStep("region");
      setShowIntents(false);
      const botMsg = "Please select your region:";
      setMessages((m) => [...m, { from: "bot", text: botMsg }]);
      setInputPlaceholder("Select a region...");
      await saveMessage("bot", botMsg);
      return;
    }

    // Leadership intent
    if (intent === "leadership") {
      const president = members.find(m => m.role === "president");
      const nc = members.filter(m => m.role === "national_council");
      const ec = members.filter(m => ecRoles.includes(m.role));
      let response = "📋 National Leadership:\n\n";
      if (president) {
        response += `🎖️ President: ${president.full_name}${president.email ? `\nEmail: ${president.email}` : ""}${president.phone ? `\nPhone: ${president.phone}` : ""}\n\n`;
      }
      if (ec.length > 0) {
        response += `Executive Committee (${ec.length} members):\n${ec.map(m => `• ${m.full_name}${roleLabel[m.role] ? ` — ${roleLabel[m.role]}` : ""}`).join("\n")}\n\n`;
      }
      if (nc.length > 0) {
        response += `National Council (${nc.length} members):\n${nc.map(m => `• ${m.full_name}`).join("\n")}`;
      }
      const text = response || "Leadership information is being updated. Please check back soon!";
      const msg: ChatMsg = {
        from: "bot",
        text,
        buttons: [{ label: "← Back to Main Menu", intent: "main_menu" }],
      };
      setMessages((m) => [...m, msg]);
      await saveMessage("bot", text, "leadership");
      setInputPlaceholder("Type your question or choose an option...");
      return;
    }

    // Prayer meetings intent
    if (intent === "prayer_meetings") {
      const msg: ChatMsg = {
        from: "bot",
        text: "🙏 Monthly Prayer Conference\n\nJoin us for powerful times of prayer and intercession.",
        buttons: [
          { label: "Join Zoom Conference", href: "https://us06web.zoom.us/j/83527683229?pwd=VW9kRnFPQ1RnV1F5RGZyZ0ZFQmROdz09" },
          { label: "← Back to Main Menu", intent: "main_menu" },
        ],
      };
      setMessages((m) => [...m, msg]);
      await saveMessage("bot", msg.text, "prayer_meetings");
      setInputPlaceholder("Type your question or choose an option...");
      return;
    }

    // Women's ministry intent
    if (intent === "womens_ministry") {
      const womensActivitiesRaw = activities.filter(a =>
        a.activity_type === "womens_ministry" ||
        a.title.toLowerCase().includes("women") ||
        a.description?.toLowerCase().includes("women")
      );

      // Remove placeholder/demo entries (e.g., example Zoom links or placeholder texts)
      const womensActivities = womensActivitiesRaw.filter(a => {
        const title = a.title?.toLowerCase() || "";
        const desc = a.description?.toLowerCase() || "";
        const link = a.zoom_link?.toLowerCase() || "";
        return !(
          /example/.test(title) ||
          /example/.test(desc) ||
          /example/.test(link)
        );
      });

      const fallbackDetails = "Zoom Fellowship Schedule:\n• Every Monday – 9:00 PM EST · 8:00 PM CST · 6:00 PM PST\n• Third Saturday (~1h15m) – 11:00 AM EST · 10:00 AM CST · 8:00 AM PST";
      let detailsText = "";
      if (womensActivities.length > 0) {
        detailsText = womensActivities.map(formatActivityDetails).join("\n\n");
      } else {
        detailsText = fallbackDetails;
      }

      const msg: ChatMsg = {
        from: "bot",
        text: `👩 Women's Ministry:\n\n${detailsText}\n\nAll women are welcome to join! See you there! 🙏`,
        buttons: [
          { label: "Open Women's Ministry Page", href: "/womens-ministry" },
          { label: "← Back to Main Menu", intent: "main_menu" },
        ],
      };
      setMessages((m) => [...m, msg]);
      await saveMessage("bot", msg.text);
      setInputPlaceholder("Type your question or choose an option...");
      return;
    }

    // Conferences intent
    if (intent === "conferences") {
      const upcomingConferences = conferences.filter(c => c.status === "upcoming");
      const latestHighlight = `Latest: OFUESI Half Yearly Conference 2025\n🔗 Join: https://ofuesi.zoom.us/j/87383053110?pwd=4AwPbX424AL5JEy0v256sDXpO9wqLn.1`;
      if (upcomingConferences.length > 0) {
        const confList = upcomingConferences.map(c => `🎉 ${c.conference_name}${c.location ? `\n📍 ${c.location}` : ""}${c.conference_date ? `\n📅 ${new Date(c.conference_date).toLocaleDateString()}` : ""}${c.description ? `\n${c.description}` : ""}`).join("\n\n");
        const msg: ChatMsg = {
          from: "bot",
          text: `Upcoming Conferences:\n\n${latestHighlight}\n\n${confList}\n\nVisit our Conferences page for registration and more details!`,
          buttons: [{ label: "← Back to Main Menu", intent: "main_menu" }],
        };
        setMessages((m) => [...m, msg]);
        await saveMessage("bot", msg.text);
      } else {
        const msg: ChatMsg = {
          from: "bot",
          text: `No upcoming conferences at the moment.\n\n${latestHighlight}\n\nCheck our Conferences page or contact your regional coordinator for updates!`,
          buttons: [{ label: "← Back to Main Menu", intent: "main_menu" }],
        };
        setMessages((m) => [...m, msg]);
        await saveMessage("bot", msg.text);
      }
      setInputPlaceholder("Type your question or choose an option...");
      return;
    }

    // KIT Keep in Touch intent
    if (intent === "kit") {
      const msg: ChatMsg = {
        from: "bot",
        text: "Stay connected with KIT (Keep In Touch)!",
        buttons: [
          { label: "Open KIT page", href: "/keep-in-touch" },
          { label: "← Back to Main Menu", intent: "main_menu" },
        ],
      };
      setMessages((m) => [...m, msg]);
      await saveMessage("bot", msg.text, "kit");
      setInputPlaceholder("Type your question or choose an option...");
      return;
    }

    // Feedback intent
    if (intent === "feedback") {
      const botMsg = "We'd love to hear your feedback! ⭐\n\nPlease rate your experience (1-5 stars):";
      setMessages((m) => [...m, { from: "bot", text: botMsg }]);
      await saveMessage("bot", botMsg, "feedback");
      setInputPlaceholder("Type a number 1-5...");
      setAwaitingFeedbackRating(true);
      setShowIntents(false);
      return;
    }

    // Default: show intents
    const fallback = "Please choose an option below or type your question.";
    setMessages((m) => [...m, { from: "bot", text: fallback }]);
    await saveMessage("bot", fallback);
    setShowIntents(true);
    setInputPlaceholder("Type your question or choose an option...");
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userText = input.trim();
    const lowerText = userText.toLowerCase();
    setMessages((m) => [...m, { from: "user", text: userText }]);
    setInput("");

    // Save user message
    await saveMessage("user", userText);

    // Handle feedback rating collection
    if (awaitingFeedbackRating) {
      const rating = parseInt(userText);
      if (rating >= 1 && rating <= 5) {
        setFeedbackRating(rating);
        setAwaitingFeedbackRating(false);
        setAwaitingFeedbackComment(true);
        const botMsg = `Thank you for rating us ${rating} star${rating > 1 ? 's' : ''}! ⭐\n\nWould you like to leave a comment? (Type your comment or 'skip')`;
        setMessages((m) => [...m, { from: "bot", text: botMsg }]);
        setInputPlaceholder("Type your comment or 'skip'...");
        await saveMessage("bot", botMsg);
      } else {
        const botMsg = "Please enter a valid rating between 1 and 5.";
        setMessages((m) => [...m, { from: "bot", text: botMsg }]);
        await saveMessage("bot", botMsg);
      }
      return;
    }

    // Handle feedback comment collection
    if (awaitingFeedbackComment) {
      setAwaitingFeedbackComment(false);
      const comment = userText.toLowerCase() === 'skip' ? null : userText;
      
      // Save feedback to database
      if (sessionId) {
        await supabase
          .from("chat_sessions")
          .update({
            feedback_rating: feedbackRating,
            feedback_comment: comment,
          })
          .eq("id", sessionId);
      }

      const botMsg = "Thank you for your feedback! It helps us serve you better. 🙏\n\nIs there anything else I can help you with?";
      setMessages((m) => [...m, { from: "bot", text: botMsg }]);
      await saveMessage("bot", botMsg);
      setShowIntents(true);
      setInputPlaceholder("Type your question or choose an option...");
      return;
    }

    // Handle first name collection
    if (awaitingFirstName) {
      setUserFirstName(userText);
      setAwaitingFirstName(false);
      setAwaitingLastName(true);
      const botMsg = "Great! Now, please tell me your last name.";
      setMessages((m) => [...m, { from: "bot", text: botMsg }]);
      setInputPlaceholder("Type your last name...");
      await saveMessage("bot", botMsg);
      return;
    }

    // Handle last name collection
    if (awaitingLastName) {
      setUserLastName(userText);
      setAwaitingLastName(false);
      setAwaitingLocation(true);
      const botMsg = `Nice to meet you, ${userFirstName} ${userText}! 🎉\n\nAre you currently in the USA or are you international?`;
      setMessages((m) => [...m, { from: "bot", text: botMsg }]);
      setInputPlaceholder("Type 'USA' or your country...");
      await saveMessage("bot", botMsg);
      return;
    }

    // Handle location verification
    if (awaitingLocation) {
      setAwaitingLocation(false);
      const userLocation = userText.toLowerCase().trim();
      
      // Check for USA keywords
      const usaKeywords = ['usa', 'us', 'u.s.', 'u.s.a.', 'united states', 'america', 'american'];
      const isUSA = usaKeywords.some(keyword => userLocation.includes(keyword));
      
      // If not USA, treat as international by default; also match common keywords
      const internationalKeywords = ['international', 'india', 'pakistan', 'bangladesh', 'nepal', 'sri lanka', 'china', 'japan', 'korea', 'europe', 'africa', 'asia', 'australia', 'canada', 'uk', 'england', 'outside', 'abroad', 'foreign'];
      const matchesInternational = internationalKeywords.some(keyword => userLocation.includes(keyword));
      const isInternational = !isUSA || matchesInternational;

      // Create chat session
      try {
        const { data: session, error } = await supabase
          .from("chat_sessions")
          .insert({
            user_first_name: userFirstName,
            user_last_name: userLastName,
            user_location: userText,
            is_international: isInternational,
          })
          .select()
          .single();

        if (error) throw error;
        setSessionId(session.id);

        if (isInternational) {
          const botMsg: ChatMsg = {
            from: "bot",
            text: "Thank you! Since you're outside the USA, please contact us and we'll assist you right away.",
            buttons: [{ label: "Contact us", action: "contact", href: "/contact" }],
          };
          setMessages((m) => [...m, botMsg]);
          await supabase.from("chat_messages").insert({
            session_id: session.id,
            message_from: "bot",
            message_text: botMsg.text,
          });
          setInputPlaceholder("Type your question or choose an option...");
        } else {
          setShowIntents(true);
          const botMsg = `Welcome to the OFUESI community, ${userFirstName}! 🙏\n\nHow can I help you today? Select an option below or type your question.`;
          setMessages((m) => [...m, { from: "bot", text: botMsg }]);
          await supabase.from("chat_messages").insert({
            session_id: session.id,
            message_from: "bot",
            message_text: botMsg,
          });
          setInputPlaceholder("Type your question or choose an option...");

          // If user clicked an intent earlier, handle it now
          if (pendingIntent) {
            const pi = pendingIntent;
            setPendingIntent(null);
            await processIntent(pi);
          }
        }
      } catch (error) {
        console.error("Error creating chat session:", error);
      }
      return;
    }

    // helpers moved to component scope for reuse across intents and text processing

    // Handle menu command
    if (matchesKeywords(lowerText, ["menu", "main menu", "options", "help", "start over", "reset", "restart"])) {
      setShowIntents(true);
      setAwaitingCity(false);
      setAwaitingState(false);
      const menuMsg = `🏠 Main Menu:\n\nHow can I help you today? Select an option below or type your question!`;
      setMessages((m) => [...m, { from: "bot", text: menuMsg }]);
      await saveMessage("bot", menuMsg);
      setInputPlaceholder("Type your question or choose an option...");
      return;
    }

    // Handle "new to USA" intent with flexible matching
    if (matchesKeywords(lowerText, ["new to usa", "new to us", "new student", "new here", "joining", "planning to join", "coming to", "moving to", "want to join"]) && !awaitingState && !awaitingCity) {
      setAwaitingCity(true);
      setShowIntents(false);
      const bibleVerse = "\"For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future.\" - Jeremiah 29:11 🙏";
      const botMsg = `Welcome to the USA! 🎉\n\n${bibleVerse}\n\nWe're excited to support you in your journey! Please tell me which city and state you're in or moving to so I can connect you with your regional fellowship.\n\n(e.g., Austin, Texas or just Portland)`;
      setMessages((m) => [
        ...m,
        { from: "bot", text: botMsg },
      ]);
      setInputPlaceholder("City, State (e.g., Austin, Texas)");
      await saveMessage("bot", botMsg);
      return;
    }

    // Handle city/state input when awaiting
    if (awaitingCity) {
      setAwaitingCity(false);
      const locationInput = userText.toLowerCase().trim();
      let foundRegion: Region | null = null;
      let foundState: string | null = null;

      // First, try to match city name from our mapping
      for (const [city, state] of Object.entries(cityToStateMap)) {
        if (locationInput.includes(city) || locationInput === city) {
          foundState = state;
          break;
        }
      }

      // If we found a state from city mapping, find the region
      if (foundState) {
        for (const r of regions) {
          if (r.states.some(s => s.toLowerCase() === foundState.toLowerCase())) {
            foundRegion = r;
            break;
          }
        }
      }

      // If not found via city, try to find state directly in the input
      if (!foundRegion) {
        for (const r of regions) {
          for (const state of r.states) {
            if (locationInput.includes(state.toLowerCase()) || locationInput === state.toLowerCase()) {
              foundRegion = r;
              foundState = state;
              break;
            }
          }
          if (foundRegion) break;
        }
      }

      if (foundRegion && foundState) {
        setRegion(foundRegion.name);

        // Get coordinator info
        const coordinatorInfo = foundRegion.coordinator_name
          ? `\n\n👤 Your Regional Coordinator:\n${foundRegion.coordinator_name}${foundRegion.coordinator_email ? `\n📧 ${foundRegion.coordinator_email}` : ""}${foundRegion.coordinator_phone ? `\n📞 ${foundRegion.coordinator_phone}` : ""}`
          : "";

        // Get Bible studies for this state
        const bibleStudies = (foundRegion as any).bible_studies || [];
        let bibleStudyInfo = "";
        if (Array.isArray(bibleStudies) && bibleStudies.length > 0) {
          const stateBibleStudies = bibleStudies.filter((s: any) => 
            s.state?.toLowerCase() === foundState?.toLowerCase() || 
            s.city?.toLowerCase().includes(locationInput)
          );
          if (stateBibleStudies.length > 0) {
            const list = stateBibleStudies.map((s: any) => 
              `• ${s.city || 'N/A'}${s.type ? ` — ${s.type}` : ""}${s.contact_name ? `\n  Contact: ${s.contact_name}` : ""}${s.contact_phone ? `\n  📞 ${s.contact_phone}` : ""}${s.contact_email ? `\n  📧 ${s.contact_email}` : ""}`
            ).join("\n\n");
            bibleStudyInfo = `\n\n📖 Bible Studies in ${foundState}:\n\n${list}`;
          }
        }

        const mainMessage = `Perfect! You're part of the ${foundRegion.name} region (${foundState}). 🎉${foundRegion.description ? `\n\n${foundRegion.description}` : ""}${coordinatorInfo}${bibleStudyInfo}`;

        const msg: ChatMsg = {
          from: "bot",
          text: mainMessage,
          buttons: [{ label: "← Back to Main Menu", intent: "main_menu" }],
        };

        setMessages((m) => [...m, msg]);
        await saveMessage("bot", mainMessage);
        setShowIntents(true);
      } else {
        const retryMsg = `I couldn't identify "${userText}". Could you try again with:\n• Just the city name (e.g., Portland, Austin, Boston)\n• Or City, State format (e.g., Portland, Oregon)\n\nOr type 'menu' to see all options.`;
        setMessages((m) => [
          ...m,
          { from: "bot", text: retryMsg },
        ]);
        await saveMessage("bot", retryMsg);
        setAwaitingCity(true);
      }
      return;
    }

    // Handle state input when awaiting (for region selection)
    if (awaitingState) {
      setAwaitingState(false);
      const stateInput = userText.toLowerCase();
      let foundRegion: Region | null = null;

      for (const r of regions) {
        if (r.states.some(s => s.toLowerCase().includes(stateInput) || stateInput.includes(s.toLowerCase()))) {
          foundRegion = r;
          break;
        }
      }

      if (foundRegion) {
        setRegion(foundRegion.name);
        const coordinator = foundRegion.coordinator_name
          ? `\n\n📞 Your Regional Coordinator:\n${foundRegion.coordinator_name}${foundRegion.coordinator_email ? `\nEmail: ${foundRegion.coordinator_email}` : ""}${foundRegion.coordinator_phone ? `\nPhone: ${foundRegion.coordinator_phone}` : ""}`
          : "";

        // Bible studies for typed state (best-effort match)
        let bibleStudyInfo = "";
        const bibleStudies = (foundRegion as any).bible_studies || [];
        if (Array.isArray(bibleStudies) && bibleStudies.length > 0) {
          const stateBibleStudies = bibleStudies.filter((s: any) =>
            String(s.state || s.state_code || "").toLowerCase().includes(stateInput) ||
            String(s.city || "").toLowerCase().includes(stateInput)
          );
          if (stateBibleStudies.length > 0) {
            const list = stateBibleStudies.map((s: any) =>
              `• ${s.city || 'N/A'}${s.state ? `, ${s.state}` : ""}${s.type ? ` — ${s.type}` : ""}${s.contact_name ? `\n  Contact: ${s.contact_name}` : ""}${s.contact_phone ? `\n  📞 ${s.contact_phone}` : ""}${s.contact_email ? `\n  📧 ${s.contact_email}` : ""}`
            ).join("\n\n");
            bibleStudyInfo = `\n\n📖 Bible Studies:\n\n${list}`;
          }
        }
        
        const botMsg = `Perfect! You'll be part of the ${foundRegion.name} region.${foundRegion.description ? `\n\n${foundRegion.description}` : ""}${coordinator}${bibleStudyInfo}\n\nFeel free to ask me about prayer meetings, activities, or other regional info!`;
        const msg: ChatMsg = {
          from: "bot",
          text: botMsg,
          buttons: [{ label: "← Back to Main Menu", intent: "main_menu" }],
        };
        setMessages((m) => [...m, msg]);
        await saveMessage("bot", botMsg);
      } else {
        const errorMsg = "I couldn't match that to a region. Could you try the full state name?";
        setMessages((m) => [
          ...m,
          { from: "bot", text: errorMsg },
        ]);
        await saveMessage("bot", errorMsg);
      }
      return;
    }

    // Handle zoom/meeting details requests - HIGH PRIORITY
    if (matchesKeywords(lowerText, ["zoom", "meeting id", "passcode", "meeting details", "join link", "zoom link", "zoom details"])) {
      const zoomActivities = activities.filter(a => a.zoom_link || a.meeting_id);
      
      if (zoomActivities.length > 0) {
        const details = zoomActivities.map(formatActivityDetails).join("\n\n");
        const botMsg = `🔗 Zoom Meeting Details:\n\n${details}\n\nJust click the Zoom link or use the Meeting ID and Passcode to join!`;
        setMessages((m) => [
          ...m,
          { from: "bot", text: botMsg },
        ]);
        await saveMessage("bot", botMsg);
      } else {
        const botMsg = "I don't have zoom details available right now. Please contact your regional coordinator or check our Contact page for assistance.";
        setMessages((m) => [
          ...m,
          { from: "bot", text: botMsg },
        ]);
        await saveMessage("bot", botMsg);
      }
      return;
    }

    // Handle women's ministry/fellowship requests - HIGH PRIORITY
    if (matchesKeywords(lowerText, ["women", "women's", "womens", "ladies", "sisters", "women fellowship", "women ministry", "women's ministry"])) {
      const womensActivities = activities.filter(a =>
        a.activity_type === "womens_ministry" ||
        a.title.toLowerCase().includes("women") ||
        a.description?.toLowerCase().includes("women")
      );

      const fallbackDetails = "Zoom Fellowship Schedule:\n• Every Monday – 9:00 PM EST · 8:00 PM CST · 6:00 PM PST\n• Third Saturday (~1h15m) – 11:00 AM EST · 10:00 AM CST · 8:00 AM PST";
      const detailsText = womensActivities.length > 0
        ? womensActivities.map(formatActivityDetails).join("\n\n")
        : fallbackDetails;

      const msg: ChatMsg = {
        from: "bot",
        text: `👩 Women's Ministry:\n\n${detailsText}\n\nAll women are welcome to join! See you there! 🙏`,
        buttons: [{ label: "Open Women's Ministry Page", href: "/womens-ministry" }],
      };
      setMessages((m) => [...m, msg]);
      await saveMessage("bot", msg.text);
      return;
    }

    // Handle president/national leadership inquiries
    if (matchesKeywords(lowerText, ["president", "national council", "executive council", "nc", "ec", "leadership", "national leadership", "national team", "executives"])) {
      const president = members.find(m => m.role === "president");
      const nc = members.filter(m => m.role === "national_council");
      const ec = members.filter(m => ecRoles.includes(m.role));

      let response = "📋 National Leadership:\n\n";
      if (president) {
        response += `🎖️ President: ${president.full_name}${president.email ? `\nEmail: ${president.email}` : ""}${president.phone ? `\nPhone: ${president.phone}` : ""}\n\n`;
      }
      if (ec.length > 0) {
        response += `Executive Committee (${ec.length} members):\n${ec.map(m => `• ${m.full_name}${roleLabel[m.role] ? ` — ${roleLabel[m.role]}` : ""}`).join("\n")}\n\n`;
      }
      if (nc.length > 0) {
        response += `National Council (${nc.length} members):\n${nc.map(m => `• ${m.full_name}`).join("\n")}`;
      }

      setMessages((m) => [...m, { from: "bot", text: response || "Leadership information is being updated. Please check back soon!" }]);
      return;
    }

    // Handle contact/coordinator inquiries
    if (matchesKeywords(lowerText, ["contact", "coordinator", "phone", "email", "info", "information", "reach out", "get in touch", "connect with"])) {
      if (!region) {
        setMessages((m) => [
          ...m,
          { from: "bot", text: "Please select a region first to get coordinator information, or type your state name to get connected!" },
        ]);
        return;
      }

      const selectedRegion = regions.find((r) => r.name === region);
      if (selectedRegion) {
        const coordinatorInfo = selectedRegion.coordinator_name
          ? `📞 Regional Coordinator for ${region}:\n\n${selectedRegion.coordinator_name}${
              selectedRegion.coordinator_email ? `\n✉️ Email: ${selectedRegion.coordinator_email}` : ""
            }${selectedRegion.coordinator_phone ? `\n📱 Phone: ${selectedRegion.coordinator_phone}` : ""}`
          : `I don't have coordinator information for ${region} yet. Please use the Contact page or check back soon.`;

        setMessages((m) => [...m, { from: "bot", text: coordinatorInfo }]);
        return;
      }

      setMessages((m) => [
        ...m,
        { from: "bot", text: `I couldn't find information for ${region}. Please try selecting another region from the dropdown.` },
      ]);
      return;
    }

    // Handle facilitator inquiries
    if (matchesKeywords(lowerText, ["facilitator", "teacher", "leader", "who leads", "who teaches", "instructor", "mentor"])) {
      if (!region) {
        setMessages((m) => [...m, { from: "bot", text: "Please select a region first, or tell me your state!" }]);
        return;
      }

      const selectedRegion = regions.find(r => r.name === region);
      const regionFacilitators = facilitators.filter(f => f.region_id === selectedRegion?.id);

      if (regionFacilitators.length > 0) {
        const response = `👥 ${region} Facilitators:\n\n${regionFacilitators.map(f => 
          `• ${f.full_name}${f.email ? `\n  ✉️ ${f.email}` : ""}${f.phone ? `\n  📱 ${f.phone}` : ""}`
        ).join("\n\n")}`;
        setMessages((m) => [...m, { from: "bot", text: response }]);
      } else {
        setMessages((m) => [...m, { from: "bot", text: `No facilitator information available for ${region} yet. Check back soon!` }]);
      }
      return;
    }

    // Handle state inquiries
    if (matchesKeywords(lowerText, ["states", "state", "location", "where", "which states", "what states", "coverage area"])) {
      if (!region) {
        setMessages((m) => [
          ...m,
          { from: "bot", text: "Select a region or tell me your state, and I'll show you which states are covered!" },
        ]);
        return;
      }
      
      const selectedRegion = regions.find((r) => r.name === region);
      if (selectedRegion && selectedRegion.states?.length > 0) {
        setMessages((m) => [
          ...m,
          { from: "bot", text: `📍 ${region} covers:\n${selectedRegion.states.join(", ")}\n\nNeed coordinator details? Just ask!` },
        ]);
        return;
      }
    }

    // Handle conference inquiries - MUST come before prayer/activities to avoid regex conflicts
    if (matchesKeywords(lowerText, ["conference", "conferences", "event", "events", "gathering", "convention"])) {
      const upcomingConferences = conferences.filter(c => c.status === "upcoming");
      
      if (upcomingConferences.length > 0) {
        const confList = upcomingConferences.map(c => 
          `🎉 ${c.conference_name}${c.location ? `\n📍 ${c.location}` : ""}${c.conference_date ? `\n📅 ${new Date(c.conference_date).toLocaleDateString()}` : ""}${c.description ? `\n${c.description}` : ""}`
        ).join("\n\n");
        setMessages((m) => [
          ...m,
          { from: "bot", text: `Upcoming Conferences:\n\n${confList}\n\nVisit our Conferences page for registration and more details!` },
        ]);
      } else {
        setMessages((m) => [
          ...m,
          { from: "bot", text: "No upcoming conferences at the moment. Check our Conferences page or contact your regional coordinator for updates!" },
        ]);
      }
      return;
    }

    // Handle regional info/contacts
    if (matchesKeywords(lowerText, ["region", "regional", "regional contact", "regional info"])) {
      setShowRegionSelector(true);
      setMessages((m) => [
        ...m,
        { from: "bot", text: "Please select your region from the dropdown above to see regional contacts and information." },
      ]);
      return;
    }

    // Handle prayer/activities inquiries with smart keyword matching
    if (matchesKeywords(lowerText, ["prayer", "pray", "praying", "praise", "intercession", "worship", "meeting", "activity", "activities", "bible study", "fellowship", "devotion", "spiritual"])) {
      const allActivities = region 
        ? activities.filter(a => a.region_id === regions.find(r => r.name === region)?.id)
        : activities.filter(a => !a.region_id); // National activities

      if (allActivities.length > 0) {
        const response = `🙏 ${region ? `${region} ` : ""}Prayer & Activities:\n\n${allActivities.map(formatActivityDetails).join("\n\n")}`;
        setMessages((m) => [...m, { from: "bot", text: response }]);
      } else if (!region) {
        // Show national/general activities if no region selected
        const nationalActivities = activities.filter(a => !a.region_id);
        if (nationalActivities.length > 0) {
          const response = `🙏 National Prayer & Activities:\n\n${nationalActivities.map(formatActivityDetails).join("\n\n")}\n\nSelect your region for regional activities!`;
          setMessages((m) => [...m, { from: "bot", text: response }]);
        } else {
          setMessages((m) => [
            ...m,
            { from: "bot", text: "We have various prayer meetings and activities. Please select your region or check our activities page for more information." },
          ]);
        }
      } else {
        setMessages((m) => [
          ...m,
          { from: "bot", text: `No activities scheduled for ${region} at the moment. Please check with your regional coordinator for updates.` },
        ]);
      }
      return;
    }

    // Generic response with retry and contact after attempts
    setShowIntents(true);
    const notSure = "I couldn't understand that. Please select an option below or retype your question.";
    setMessages((m) => [
      ...m,
      { from: "bot", text: notSure },
      ...(unknownAttempts + 1 >= 3
        ? [{ from: "bot", text: "Need more help? You can contact us directly.", buttons: [{ label: "Contact us", action: "contact", href: "/contact" }] } as ChatMsg]
        : []),
    ]);
    await saveMessage("bot", notSure);
    setUnknownAttempts((c) => c + 1);
  };

  const handleIntentClick = (intentKey: string) => {
    setShowIntents(false);
    // Do not type into input; guide user and handle intent directly
    processIntent(intentKey);
  };

  const handleClose = () => {
    setOpen(false);
    setShowIntents(false);
    setShowRegionSelector(false);
    setAwaitingFirstName(false);
    setAwaitingLastName(false);
    setAwaitingLocation(false);
    setUserFirstName("");
    setUserLastName("");
    setSessionId(null);
    setUnknownAttempts(0);
    setPendingIntent(null);
    setInputPlaceholder("Type your message...");
    setOnboardingStep("country");
    setSelectedCountry("");
    setCountryOther("");
    setStateProvince("");
    setUserEmail("");
    setUserPhone("");
    setMessages([
      { from: "bot", text: "Welcome to OFUESI! 👋\n\nPlease select your location below to get started." },
    ]);
  };

  const handleMinimize = () => {
    setMinimized(true);
    setOpen(false);
  };

  const handleReopen = () => {
    setOpen(true);
    setMinimized(false);
  };

  return (
    <div className="fixed bottom-20 right-4 z-50 md:bottom-4">
      {open ? (
        <Card className="w-[280px] sm:w-[320px] h-[350px] sm:h-[450px] flex flex-col shadow-xl border-0">
          <div className="px-4 py-3 border-b-0 flex items-center justify-between bg-gradient-to-r from-ministry-blue to-ministry-purple text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                <img src={ofuesiLogo} alt="OFUESI Logo" className="w-full h-full object-contain" />
              </div>
              <div className="font-semibold text-base">OFUESI Help Desk</div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleMinimize} aria-label="Minimize" className="p-1 hover:opacity-80">
                <Minus className="w-4 h-4" />
              </button>
              <button onClick={handleClose} aria-label="Close" className="p-1 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {showRegionSelector && (
            <div className="px-4 pt-3">
              <label className="text-xs text-muted-foreground">Region</label>
              <select
                className="w-full mt-1 mb-2 p-2 rounded-md border bg-background"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                disabled={loading}
              >
                <option value="">Select a region</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div ref={containerRef} className="px-4 flex-1 overflow-y-auto space-y-3 pb-2">
            {messages.map((m, i) => (
              <div key={i}>
                <div className="flex items-start gap-2 mb-2">
                  {m.from === "bot" ? (
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                      <img src={ofuesiLogo} alt="OFUESI" className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <Avatar className="w-8 h-8 flex-shrink-0">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {userFirstName && userLastName 
                          ? `${userFirstName[0]}${userLastName[0]}`.toUpperCase()
                          : "U"}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div className="flex-1">
                    <div className={`text-sm ${m.from === "bot" ? "text-muted-foreground" : "text-foreground"} whitespace-pre-line`}>
                      <span className="font-medium">{m.from === "bot" ? "OFUESI" : userFirstName || "You"}:</span> {m.text}
                    </div>
                    {m.buttons && m.buttons.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.buttons.map((b, bi) => (
                          <Button
                            key={bi}
                            size="sm"
                            variant="default"
                            onClick={() => {
                              if (b.href) {
                                // Open PDFs and Zoom links in new tab, navigate to pages normally
                                if (b.href.endsWith('.pdf') || b.href.includes('zoom.us')) {
                                  window.open(b.href, '_blank');
                                } else {
                                  window.location.href = b.href;
                                }
                              } else if (b.action === "contact") {
                                window.location.href = "/contact";
                              } else if (b.intent) {
                                processIntent(b.intent);
                              }
                            }}
                          >
                            {b.label}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Show country selector for onboarding */}
                {m.from === "bot" && i === messages.length - 1 && onboardingStep === "country" && (
                  <div className="mt-3 space-y-2">
                    <select
                      className="w-full p-2 rounded-md border bg-background text-sm"
                      value={selectedCountry}
                      onChange={(e) => {
                        const country = e.target.value;
                        setSelectedCountry(country);
                        if (country) {
                          setOnboardingStep("userinfo");
                          setMessages((m) => [
                            ...m,
                            { from: "user", text: country },
                            { from: "bot", text: `Great! Please provide your details below.` },
                          ]);
                        }
                      }}
                    >
                      <option value="">Select your country</option>
                      <option value="USA">🇺🇸 United States</option>
                      <option value="India">🇮🇳 India</option>
                      <option value="Canada">🇨🇦 Canada</option>
                      <option value="UK">🇬🇧 United Kingdom</option>
                      <option value="Other">🌍 Other</option>
                    </select>
                  </div>
                )}

                {/* Show user info form */}
                {m.from === "bot" && i === messages.length - 1 && onboardingStep === "userinfo" && (
                  <div className="mt-3 space-y-3">
                    <Input
                      placeholder="First Name"
                      value={userFirstName}
                      onChange={(e) => setUserFirstName(e.target.value)}
                      className="text-sm"
                    />
                    <Input
                      placeholder="Last Name"
                      value={userLastName}
                      onChange={(e) => setUserLastName(e.target.value)}
                      className="text-sm"
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      className="text-sm"
                    />
                    <div className="phone-input-wrapper">
                      <PhoneInput
                        international
                        defaultCountry={selectedCountry === "USA" ? "US" : selectedCountry === "India" ? "IN" : undefined}
                        value={userPhone}
                        onChange={(value) => setUserPhone(value || "")}
                        className="text-sm"
                      />
                    </div>
                    <Button
                      className="w-full"
                      onClick={async () => {
                        if (!userFirstName || !userLastName || !userEmail) {
                          alert("Please fill in all required fields");
                          return;
                        }
                        
                        // Create session
                        const { data: sessionData, error: sessionError } = await supabase
                          .from("chat_sessions")
                          .insert({
                            user_first_name: userFirstName,
                            user_last_name: userLastName,
                            user_location: selectedCountry,
                            is_international: selectedCountry !== "USA",
                          })
                          .select()
                          .single();

                        if (!sessionError && sessionData) {
                          setSessionId(sessionData.id);
                        }

                        if (selectedCountry === "USA") {
                          setOnboardingStep("chat");
                          setShowIntents(true);
                          setMessages((m) => [
                            ...m,
                            { from: "user", text: `${userFirstName} ${userLastName}` },
                            { from: "bot", text: `Welcome ${userFirstName}! 👋\n\nHow can I help you today?` },
                          ]);
                        } else {
                          setMessages((m) => [
                            ...m,
                            { from: "user", text: `${userFirstName} ${userLastName}` },
                            { from: "bot", text: `Thank you ${userFirstName}! For international support, please visit our contact page.` },
                          ]);
                          setOnboardingStep("chat");
                          setTimeout(() => {
                            window.location.href = "/contact";
                          }, 1500);
                        }
                      }}
                    >
                      Continue
                    </Button>
                  </div>
                )}

                {/* Show region selector for Regional Contacts intent */}
                {m.from === "bot" && i === messages.length - 1 && onboardingStep === "region" && (
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {regions.map((r) => (
                      <Button
                        key={r.id}
                        variant="default"
                        size="sm"
                        className="h-auto py-3"
                        onClick={async () => {
                          setRegion(r.name);
                          setOnboardingStep("chat");
                          setShowIntents(true);
                          
                          const selectedRegion = regions.find(rg => rg.name === r.name) as any;
                          const coordinatorInfo = selectedRegion?.coordinator_name
                            ? `\n\n👤 Regional Coordinator:\n${selectedRegion.coordinator_name}${selectedRegion.coordinator_email ? `\n📧 ${selectedRegion.coordinator_email}` : ""}${selectedRegion.coordinator_phone ? `\n📞 ${selectedRegion.coordinator_phone}` : ""}`
                            : "";
                          
                          const statesInfo = selectedRegion?.states?.length 
                            ? `\n\n📍 States covered:\n${selectedRegion.states.join(", ")}`
                            : "";

                          // Bible studies list (all for region)
                          let bibleStudyInfo = "";
                          const bibleStudies = selectedRegion?.bible_studies || [];
                          if (Array.isArray(bibleStudies) && bibleStudies.length > 0) {
                            const list = bibleStudies.map((s: any) => 
                              `• ${s.city || 'N/A'}${s.state ? `, ${s.state}` : ""}${s.type ? ` — ${s.type}` : ""}${s.contact_name ? `\n  Contact: ${s.contact_name}` : ""}${s.contact_phone ? `\n  📞 ${s.contact_phone}` : ""}${s.contact_email ? `\n  📧 ${s.contact_email}` : ""}`
                            ).join("\n\n");
                            bibleStudyInfo = `\n\n📖 Bible Studies:\n\n${list}`;
                          }
                          
                          setMessages((m) => [
                            ...m,
                            { from: "user", text: r.name },
                            { from: "bot", text: `📋 ${r.name} Region${selectedRegion?.description ? `\n\n${selectedRegion.description}` : ""}${coordinatorInfo}${statesInfo}${bibleStudyInfo}\n\nHow else can I help you?` },
                          ]);
                          
                          await saveMessage("user", r.name);
                          await saveMessage("bot", `${r.name} Region details provided`);
                        }}
                      >
                        <span className="text-xs font-medium text-primary-foreground">{r.name}</span>
                      </Button>
                    ))}
                  </div>
                )}

                {/* Show intent buttons after onboarding */}
                {m.from === "bot" && i === messages.length - 1 && showIntents && onboardingStep === "chat" && (
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <Button variant="default" size="sm" className="h-auto py-3" onClick={() => handleIntentClick("main_menu")}>
                      <span className="text-xs font-medium text-primary-foreground">Main Menu</span>
                    </Button>
                    <Button variant="default" size="sm" className="h-auto py-3" onClick={() => handleIntentClick("new_to_usa")}>
                      <span className="text-xs font-medium text-primary-foreground">New to USA</span>
                    </Button>
                    <Button variant="default" size="sm" className="h-auto py-3" onClick={() => handleIntentClick("regional_contacts")}>
                      <span className="text-xs font-medium text-primary-foreground">Regional Contacts</span>
                    </Button>
                    <Button variant="default" size="sm" className="h-auto py-3" onClick={() => handleIntentClick("contact_helpdesk")}>
                      <span className="text-xs font-medium text-primary-foreground">Contact Helpdesk</span>
                    </Button>
                    <Button variant="default" size="sm" className="h-auto py-3" onClick={() => handleIntentClick("leadership")}>
                      <span className="text-xs font-medium text-primary-foreground">Leadership</span>
                    </Button>
                    <Button variant="default" size="sm" className="h-auto py-3" onClick={() => handleIntentClick("prayer_meetings")}>
                      <span className="text-xs font-medium text-primary-foreground">Prayer Meetings</span>
                    </Button>
                    <Button variant="default" size="sm" className="h-auto py-3" onClick={() => handleIntentClick("womens_ministry")}>
                      <span className="text-xs font-medium text-primary-foreground">Women's Ministry</span>
                    </Button>
                    <Button variant="default" size="sm" className="h-auto py-3" onClick={() => handleIntentClick("conferences")}>
                      <span className="text-xs font-medium text-primary-foreground">Conferences</span>
                    </Button>
                    <Button variant="default" size="sm" className="h-auto py-3" onClick={() => handleIntentClick("kit")}>
                      <span className="text-xs font-medium text-primary-foreground">KIT - Keep In Touch</span>
                    </Button>
                    <Button variant="outline" size="sm" className="h-auto py-3" onClick={() => handleIntentClick("feedback")}>
                      <span className="text-xs font-medium">Give Feedback ⭐</span>
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="p-3 border-t flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={inputPlaceholder}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <Button
              onClick={handleSend}
              className="bg-[hsl(220,70%,30%)] hover:bg-[hsl(220,70%,25%)] text-white"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <div className="px-4 pb-2">
            <p className="text-[10px] text-muted-foreground text-center">Powered by fifthaisolutions.ai</p>
          </div>
        </Card>
      ) : (
        <Button
          onClick={handleReopen}
          size="lg"
          className="rounded-full border-white text-white hover:bg-white hover:text-primary hover:border-primary hover:border-2"
        >
          <MessageCircle className="mr-2 w-5 h-5" /> Help
        </Button>
      )}
    </div>
  );
};

export default ChatbotWidget;
